import type { StructuredSchema } from "./structured-schema";
import type { CardCategoryDocument, CardTemplateDocument, StructuredCardDocument, StructuredCardSource, StructuredCardStatus } from "./product";
import type { JsonObject, JsonValue, WorldDocument, WorldReference } from "./types";

const clone = <T>(value: T): T => structuredClone(value);

function isoTimestamp(value: unknown, fallback: string): string {
  if (typeof value === "string" && !Number.isNaN(Date.parse(value))) return new Date(value).toISOString();
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return new Date(value).toISOString();
  return fallback;
}

function positiveInteger(value: unknown, fallback: number): number {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
}

function jsonValue(value: unknown): JsonValue {
  if (value == null || typeof value === "string" || typeof value === "boolean") return value as JsonValue;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) return value.map(jsonValue);
  if (typeof value === "object") return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined).map(([key, entry]) => [key, jsonValue(entry)]));
  return String(value);
}

function referencesForCard(card: StructuredCardDocument, template: CardTemplateDocument): WorldReference[] {
  const references: WorldReference[] = Array.isArray(card.canonicalReferences) ? card.canonicalReferences.filter((value): value is WorldReference => Boolean(value && typeof value === "object" && typeof value.relation === "string" && typeof value.targetId === "string")).filter(reference => !template.fields.some(field => (reference.relation === field.key || reference.relation.startsWith(`${field.key}.`)) && (field.type === "reference" || field.schema))).map(clone) : [];
  const collect = (raw: unknown, schema: StructuredSchema, relation: string): void => {
    if (raw == null) return;
    if (schema.reference || schema.referencedFileTypeSlug) {
      const values = Array.isArray(raw) ? raw : [raw];
      for (const value of values) { const id = typeof value === "string" ? value : value && typeof value === "object" ? (value as Record<string, unknown>).id ?? (value as Record<string, unknown>).slug : null; if (typeof id === "string" && id.trim()) references.push({ relation, targetId: id.trim() }); }
    } else if (Array.isArray(raw) && schema.items) raw.forEach(value => collect(value, schema.items!, relation));
    else if (raw && typeof raw === "object" && schema.properties) for (const [key, child] of Object.entries(schema.properties)) collect((raw as Record<string, unknown>)[key], child, `${relation}.${key}`);
  };
  for (const field of template.fields) collect(card.values[field.key], (field.schema as StructuredSchema | undefined) ?? (field.type === "reference" ? { reference: {} } : {}), field.key);
  return references.filter((reference, index, all) => all.findIndex((candidate) => candidate.relation === reference.relation && candidate.targetId === reference.targetId) === index);
}

/** Compatibility bridge while Studio cards migrate to canonical WorldDocument storage. */
export function structuredCardToWorldDocument(
  card: StructuredCardDocument,
  category: CardCategoryDocument,
  template: CardTemplateDocument,
  storyId: string,
  now = new Date().toISOString(),
): WorldDocument {
  const fields = jsonValue(card.values) as JsonObject;
  const declared = new Set(template.fields.map(field => field.key));
  if (declared.has("name") && !fields.name && card.title) fields.name = card.title;
  if (declared.has("title") && !fields.title && card.title) fields.title = card.title;
  if (declared.has("summary") && !fields.summary && card.summary) fields.summary = card.summary;
  if (declared.has("description") && !fields.description && card.summary) fields.description = card.summary;
  if (!("image" in fields) && card.images[0]) fields.image = card.images[0];
  if (!("images" in fields) && card.images.length) fields.images = [...card.images];
  return {
    id: card.id,
    storyId,
    kind: template.designation.trim() || category.name.trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}_.:-]+/gu, "_") || "custom_file",
    fileTypeId: category.id,
    schemaVersion: template.version,
    revision: positiveInteger(card.revision, 1),
    title: card.title.trim() || category.name,
    summary: card.summary,
    fields,
    references: referencesForCard(card, template),
    ...(card.canonicalRuntimeState && typeof card.canonicalRuntimeState === "object" ? { runtimeState: clone(card.canonicalRuntimeState) as JsonObject } : {}),
    visibility: card.contextVisibility ?? template.contextVisibility,
    displayVisibility: card.visibleToPlayers ? "player" : "creator",
    editableBy: template.agentEditable === false ? ["creator"] : ["creator", "gm", "system"],
    ...(card.triggers.length ? { tags: [...card.triggers] } : {}),
    createdAt: isoTimestamp(card.createdAt, isoTimestamp(card.updatedAt, now)),
    updatedAt: isoTimestamp(card.updatedAt, now),
  };
}

function imagesFromDocument(document: WorldDocument): string[] {
  const images = document.fields.images;
  if (Array.isArray(images)) return images.filter((value): value is string => typeof value === "string");
  const image = document.fields.image;
  return typeof image === "string" && image ? [image] : [];
}

export interface StructuredCardAdapterOptions {
  categoryId: string;
  templateId: string;
  templateVersion: number;
  source?: StructuredCardSource;
  status?: StructuredCardStatus;
  updatedAt?: number;
}

export function worldDocumentToStructuredCard(document: WorldDocument, options: StructuredCardAdapterOptions): StructuredCardDocument {
  return {
    id: document.id,
    categoryId: options.categoryId,
    templateId: options.templateId,
    templateVersion: options.templateVersion,
    title: document.title,
    values: clone(document.fields),
    canonicalReferences: clone(document.references),
    ...(document.runtimeState ? { canonicalRuntimeState: clone(document.runtimeState) } : {}),
    status: options.status ?? "ready",
    source: options.source ?? "import",
    summary: document.summary,
    images: imagesFromDocument(document),
    triggers: [...(document.tags ?? document.aliases ?? [])],
    contextVisibility: document.visibility,
    enabled: !document.deletedAt,
    visibleToPlayers: document.displayVisibility !== "creator" && document.displayVisibility !== "gm" && document.displayVisibility !== "hidden",
    revision: document.revision,
    createdAt: Date.parse(document.createdAt),
    updatedAt: options.updatedAt ?? Date.parse(document.updatedAt),
  };
}
